import { ApartmentTypeDto, ProductDto, ProductTypeDto, RoomTypeDto } from 'api'
import {
  useCustomer,
  useGetApartmentInfo,
  useGetSelections,
  useSaveSelection,
} from 'queries/queries'
import { hasSelection } from 'queries/utilities'
import { Reducer, useCallback, useEffect, useMemo, useReducer } from 'react'
import { asContext } from 'utilities/as-context'
import cacheImages from 'utilities/cache-images'
import { getProjectId } from 'utilities/get-project-id'

const useSelectReducer = ({ apartmentType }: { apartmentType: ApartmentTypeDto }) => {
  const firstRoomType = useMemo<RoomTypeDto | undefined>(
    () => apartmentType.roomTypes[0],
    [apartmentType]
  )

  if (!firstRoomType) throw Error('No room type found on apartment type: ' + apartmentType.id)

  const firstProductType = useMemo<ProductTypeDto | undefined>(
    () => firstRoomType.productTypes[0],
    [firstRoomType]
  )

  if (!firstProductType) throw Error('No product type found on room type: ' + firstRoomType.id)

  const getAdjecentRooms = useCallback(
    (roomTypeId: number) => {
      const index = apartmentType.roomTypes.findIndex((roomType) => roomType.id === roomTypeId)
      if (index === -1) return [undefined, undefined] as const
      const prevRoom = apartmentType.roomTypes[index - 1]
      const nextRoom = apartmentType.roomTypes[index + 1]
      return [prevRoom, nextRoom] as const
    },
    [apartmentType]
  )

  const [prevRoom, nextRoom] = useMemo(
    () => getAdjecentRooms(firstRoomType.id),
    [firstRoomType, getAdjecentRooms]
  )

  type Action =
    | { type: 'selectRoomType'; roomTypeId: number }
    | { type: 'selectProductType'; productTypeId: number }

  const initialState = {
    nextRoom,
    prevRoom,
    selectedRoomType: firstRoomType,
    selectedProductType: firstProductType,
  }

  const reducer: Reducer<typeof initialState, Action> = (prevState, action) => {
    switch (action.type) {
      case 'selectRoomType': {
        if (action.roomTypeId === prevState.selectedRoomType.id) return prevState
        const roomType = apartmentType.roomTypes.find(
          (roomType) => roomType.id === action.roomTypeId
        )
        const firstProductType = roomType?.productTypes[0]
        if (!firstProductType) return prevState
        cacheImages(firstProductType.products.flatMap((product) => product.imageUrl))
        const [prevRoom, nextRoom] = getAdjecentRooms(roomType.id)
        return {
          ...prevState,
          nextRoom,
          prevRoom,
          selectedRoomType: roomType,
          selectedProductType: firstProductType,
        }
      }
      case 'selectProductType': {
        if (action.productTypeId === prevState.selectedProductType.id) return prevState
        const productType = prevState.selectedRoomType.productTypes.find(
          (productType) => productType.id === action.productTypeId
        )
        if (!productType) return prevState
        cacheImages(productType.products.flatMap((product) => product.imageUrl))
        return {
          ...prevState,
          selectedProductType: productType,
        }
      }
      default: {
        return prevState
      }
    }
  }

  return useReducer(reducer, initialState)
}

function useAddonSelector() {
  const projectId = getProjectId()
  const { data: customer } = useCustomer({ projectId })

  if (!customer) throw Error('No customer found on project id: ' + projectId)

  const { data: apartmentType } = useGetApartmentInfo({
    projectId,
    customerId: customer.id,
  })

  if (!apartmentType) throw Error('No apartment type found on project id: ' + projectId)

  const [{ selectedProductType, selectedRoomType, nextRoom, prevRoom }, dispatch] =
    useSelectReducer({ apartmentType })

  const { mutateAsync } = useSaveSelection({
    projectId,
    customerId: customer?.id,
  })

  const { data: selections, refetch: refetchSelections } = useGetSelections({
    projectId,
    customerId: customer?.id,
  })

  useEffect(() => {
    const doStuff = async () => {
      const firstProduct = selectedProductType.products[0]
      if (!firstProduct) return

      const { data: selections } = await refetchSelections()

      if (!selections) return
      if (hasSelection(selections, selectedProductType, selectedRoomType)) return
      await mutateAsync({
        productId: firstProduct.id,
        productTypeId: selectedProductType.id,
        roomId: selectedRoomType.id,
      })
    }
    doStuff()
  }, [mutateAsync, refetchSelections, selectedProductType, selectedRoomType])

  const selectRoomType = useCallback(
    (roomTypeId: number) => {
      dispatch({ type: 'selectRoomType', roomTypeId })
    },
    [dispatch]
  )
  const selectProductType = useCallback(
    (productTypeId: number) => {
      dispatch({ type: 'selectProductType', productTypeId })
    },
    [dispatch]
  )
  const selectProduct = useCallback(
    (productId: number) => {
      return mutateAsync({
        productId,
        productTypeId: selectedProductType.id,
        roomId: selectedRoomType.id,
      })
    },
    [mutateAsync, selectedProductType.id, selectedRoomType.id]
  )

  const nextRoomWraper = useMemo(() => {
    if (!nextRoom) return undefined
    return {
      ...nextRoom,
      navigate: () => selectRoomType(nextRoom.id),
    }
  }, [nextRoom, selectRoomType])

  const previousRoomWraper = useMemo(() => {
    if (!prevRoom) return undefined
    return {
      ...prevRoom,
      navigate: () => selectRoomType(prevRoom.id),
    }
  }, [prevRoom, selectRoomType])

  /**
   * Check if product is first in a product type. If no
   * product type is provided, selected product type will be used.
   */
  const isProductFirst = useCallback(
    ({ productType, product }: { productType: ProductTypeDto; product: ProductDto }) => {
      const productIndex = productType.products.findIndex(({ id }) => id === product.id)
      return productIndex != null && productIndex === 0
    },
    []
  )

  /**
   * Check if product is selected in a product type. If no
   * product type is provided, selected product type will be used.
   */
  const isProductSelected = useCallback(
    ({
      roomType,
      productType,
      product,
    }: {
      roomType: RoomTypeDto
      productType: ProductTypeDto
      product: ProductDto
    }) => {
      return (
        selections?.some(
          (selection) =>
            selection.roomId === roomType.id &&
            selection.productTypeId === productType.id &&
            selection.productId === product.id
        ) ?? false
      )
    },
    [selections]
  )

  const selectedProduct = useMemo(
    () =>
      selectedProductType.products.find((product) =>
        isProductSelected({ product, productType: selectedProductType, roomType: selectedRoomType })
      ),
    [isProductSelected, selectedProductType, selectedRoomType]
  )

  const getSelectedProductFor = useCallback(
    ({ roomType, productType }: { roomType: RoomTypeDto; productType: ProductTypeDto }) => {
      return productType.products.find((product) =>
        isProductSelected({ product, productType, roomType })
      )
    },
    [isProductSelected]
  )

  // Filter out only the selected products and flaten all into on array
  const getAllSelectedProducts = useCallback(() => {
    if (!selections) return []
    const selectedProducts: ProductDto[] = []
    for (const selection of selections) {
      const roomType = apartmentType.roomTypes.find((p) => p.id === selection.roomId)
      const productType = roomType?.productTypes.find((p) => p.id === selection.productTypeId)
      const product = productType?.products.find((p) => p.id === selection.productId)
      if (product === undefined) continue
      selectedProducts.push(product)
    }
    return selectedProducts
  }, [apartmentType.roomTypes, selections])

  const productsSum = useMemo(
    () =>
      getAllSelectedProducts()
        .filter((product) => product.price !== -1) // Filter out only products which have a price (-1 indicates "According to offer")
        .reduce((acc, product) => acc + product.price, 0), // Accumulate price for each selected product
    [getAllSelectedProducts]
  )

  const hasOfferedPrice = useMemo(
    () => getAllSelectedProducts().some((product) => product.price === -1),
    [getAllSelectedProducts]
  )

  return useMemo(
    () => ({
      state: {
        selectedRoomType,
        selectedProductType,
        nextRoom: nextRoomWraper,
        previousRoom: previousRoomWraper,
        apartmentType,
        selectedProduct,
        productsSum,
        hasOfferedPrice,
      },
      actions: {
        selectProduct,
        selectRoomType,
        selectProductType,
        refetchSelections,
        isProductFirst,
        isProductSelected,
        getSelectedProductFor,
        getAllSelectedProducts,
      },
    }),
    [
      selectedRoomType,
      selectedProductType,
      nextRoomWraper,
      previousRoomWraper,
      apartmentType,
      selectedProduct,
      productsSum,
      hasOfferedPrice,
      selectProduct,
      selectRoomType,
      selectProductType,
      refetchSelections,
      isProductFirst,
      isProductSelected,
      getSelectedProductFor,
      getAllSelectedProducts,
    ]
  )
}

export const AddonSelectorContext = asContext(useAddonSelector, 'AddonSelectorContext')
